import { ref, type Ref, watch } from 'vue'
import { stakeholdersSearchRequest } from './stakeholder-search-api-calls'
import { createContext } from 'reka-ui'
import {
  type CurrentSearch,
  buildStakeholderFilterQuery,
  snapshotStakeholderFilters,
  useStakeholderSearchFilters,
  getCurrentSearchFromFilters,
  type StakeholderSearchFilterActiveState,
  type StakeholderSearchFilterKeys
} from './stakeholder-search-filters'
import { areFiltersDirty } from '../model/search-decisions'
import type {
  Stakeholder,
  StakeholderType,
  SearchResult
} from '@gat-ship/types'
import {
  IconArchive,
  IconCircleDot,
  IconClient,
  IconLayers,
  IconOrganization,
  IconVendor,
  type IconComponent
} from '@gat-ship/icons'
import { useDebounceFn } from '@vueuse/core'

export type StakeholderSearchFilterType = 'client' | 'vendor' | 'other'

export interface StakeholderSearchFilterTypeMetadata {
  title: string
  subtitle: string
  icon: IconComponent
}
export const stakeholderSearchFilterTypeMetadata: Record<
  StakeholderSearchFilterType,
  StakeholderSearchFilterTypeMetadata
> = {
  client: {
    title: 'Client',
    subtitle: 'Show only clients',
    icon: IconClient
  },
  vendor: {
    title: 'Vendor',
    subtitle: 'Show only vendors',
    icon: IconVendor
  },
  other: {
    title: 'Other',
    subtitle: 'Show only other parties',
    icon: IconOrganization
  }
}

export type StakeholderSearchFilterStatus = 'active' | 'inactive' | 'any'

export interface StakeholderSearchFilterStatusMetadata {
  title: string
  subtitle: string
  icon: IconComponent
}

export const statusMetadata: Record<
  StakeholderSearchFilterStatus,
  StakeholderSearchFilterStatusMetadata
> = {
  active: {
    title: 'Active',
    subtitle: 'Show only active stakeholders',
    icon: IconCircleDot
  },
  inactive: {
    title: 'Archived',
    subtitle: 'Show only archived stakeholders',
    icon: IconArchive
  },
  any: {
    title: 'Both',
    subtitle: 'Show both active and archived stakeholders',
    icon: IconLayers
  }
}

export type AdvancedSearchStatus = 'editing' | 'searching' | 'finished'

interface SearchState {
  searchStatus: Ref<AdvancedSearchStatus>
  setSearchStatus: (status: AdvancedSearchStatus) => void
  currentSearch: Ref<CurrentSearch | undefined>
  setCurrentSearch: (search: CurrentSearch | undefined) => void
  searchResults: Ref<SearchResult<Stakeholder>[] | undefined>
  setSearchResults: (results: SearchResult<Stakeholder>[] | undefined) => void
  clearSearchResults: () => void
}

function useSearchState(): SearchState {
  const searchStatus = ref<AdvancedSearchStatus>('editing')

  function setSearchStatus(status: AdvancedSearchStatus) {
    searchStatus.value = status
  }

  const currentSearch = ref<CurrentSearch | undefined>(undefined)
  function setCurrentSearch(search: CurrentSearch | undefined) {
    currentSearch.value = search
  }
  const searchResults = ref<SearchResult<Stakeholder>[] | undefined>(undefined)
  function setSearchResults(results: SearchResult<Stakeholder>[] | undefined) {
    searchResults.value = results
  }

  function clearSearchResults() {
    searchResults.value = undefined
  }

  return {
    searchStatus,
    setSearchStatus,
    currentSearch,
    setCurrentSearch,
    searchResults,
    setSearchResults,
    clearSearchResults
  }
}

function useQuickSearchState(): SearchState & {
  callSearch: (
    inputValue: string | undefined | null,
    signal?: AbortSignal
  ) => Promise<void>
  searchInputValue: Ref<string | undefined>
} {
  const activeAbortController = ref<AbortController | undefined>(undefined)
  const pendingSearchValue = ref<string | undefined | null>(undefined)

  function abortActiveSearch() {
    if (activeAbortController.value) {
      activeAbortController.value.abort()
      activeAbortController.value = undefined
    }
  }

  const searchState = useSearchState()

  const searchInputValue = ref<string | undefined>(undefined)

  async function callSearch(
    inputValue: string | undefined | null,
    signal?: AbortSignal
  ) {
    if (!inputValue?.trim()) {
      // searchState.clearSearchResults()
      searchState.setSearchStatus('editing')
      return
    }
    if (
      searchState.searchResults.value &&
      searchState.searchResults.value === undefined
    ) {
      searchState.setSearchStatus('searching')
    }
    searchState.setCurrentSearch({
      searchTerm: inputValue,
      activeFilters: {}
    })

    try {
      const response = await stakeholdersSearchRequest(
        {
          query: `${inputValue}`,
          queryMode: 'free-text'
        },
        {
          signal
        }
      )
      searchState.setSearchResults(response)
      searchState.setSearchStatus('finished')
      // Bespoke debounced-search-with-abort, faithfully ported from the prior
      // search provider; intentionally not refactored onto the shared
      // useDebouncedSearchInput so the search behavior stays byte-for-byte.
      // fallow-ignore-next-line code-duplication
    } catch (error) {
      if ((error as { code?: string } | null)?.code === 'ERR_CANCELED') {
        return
      }
      searchState.setSearchStatus('finished')
      throw error
    }
  }

  const debouncedCallSearch: ((value: string | undefined | null) => void) & {
    cancel?: () => void
  } = useDebounceFn((value: string | undefined | null) => {
    // Check if the search value has changed since this was scheduled
    // If it has, don't execute (a new debounced call will handle it)
    if (pendingSearchValue.value !== value) {
      return
    }
    const abortController = new AbortController()
    activeAbortController.value = abortController
    callSearch(value, abortController.signal)
  }, 500)

  watch(searchInputValue, (value) => {
    abortActiveSearch()
    // Cancel any pending debounced call
    if (
      'cancel' in debouncedCallSearch &&
      typeof debouncedCallSearch.cancel === 'function'
    ) {
      debouncedCallSearch.cancel()
    }
    // Update the pending search value
    pendingSearchValue.value = value
    if (value?.trim()) {
      searchState.setSearchStatus('searching')
      debouncedCallSearch(value)
    } else {
      searchState.clearSearchResults()
      searchState.setSearchStatus('editing')
    }
  })

  return {
    ...searchState,
    callSearch,
    searchInputValue
  }
}

function useAdvancedSearchState(): SearchState & {
  searchFilters: ReturnType<typeof useStakeholderSearchFilters>
  setSearchFilters: (filters: CurrentSearch['activeFilters']) => void
  currentSearch: Ref<CurrentSearch | undefined>
  setCurrentSearch: (search: CurrentSearch | undefined) => void
  clearCurrentSearch: () => void
  editCurrentSearch: () => void
  callFiltersSearch: () => Promise<void>
  isFiltersDirty: () => boolean
  isSearchPossible: () => boolean
  clearSearch: () => void
} {
  const searchFilters = useStakeholderSearchFilters()

  function setSearchFilters(filters: CurrentSearch['activeFilters']) {
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        searchFilters[key as StakeholderSearchFilterKeys].filterValue.value =
          value.filterValue
        searchFilters[key as StakeholderSearchFilterKeys].operator.value =
          value.operator
        searchFilters[key as StakeholderSearchFilterKeys].fieldName =
          value.fieldName
      })
    }
  }

  function resetSearchFilters() {
    Object.values(searchFilters).forEach((filter) => {
      if (filter.fieldName === 'type') {
        filter.filterValue.value = 'client' satisfies StakeholderType
        return
      }
      if (filter.fieldName === 'state') {
        filter.filterValue.value =
          'active' satisfies StakeholderSearchFilterActiveState
        return
      }
      filter.filterValue.value = undefined
    })
  }

  const searchState = useSearchState()

  const currentSearch = ref<CurrentSearch | undefined>(undefined)

  function setCurrentSearch(search: CurrentSearch | undefined) {
    currentSearch.value = search
  }

  function editCurrentSearch() {
    searchState.setSearchStatus('editing')
  }

  function clearCurrentSearch() {
    searchState.setSearchStatus('editing')
    searchState.setCurrentSearch(undefined)
    resetSearchFilters()
  }

  async function callFiltersSearch() {
    searchState.setSearchStatus('searching')
    const query = buildStakeholderFilterQuery(searchFilters)
    const currentSearch = getCurrentSearchFromFilters(searchFilters)
    currentSearch.searchTerm = query
    setCurrentSearch(currentSearch)
    const response = await stakeholdersSearchRequest({
      query,
      queryMode: 'fields'
    })
    searchState.setSearchResults(response)
    searchState.setSearchStatus('finished')
  }

  function isFiltersDirty() {
    return areFiltersDirty(snapshotStakeholderFilters(searchFilters))
  }

  function isSearchPossible() {
    return isFiltersDirty()
  }

  function clearSearch() {
    clearCurrentSearch()
    searchState.clearSearchResults()
    searchState.setSearchStatus('editing')
  }

  return {
    ...searchState,
    searchFilters,
    setSearchFilters,
    currentSearch,
    setCurrentSearch,
    clearCurrentSearch,
    editCurrentSearch,
    callFiltersSearch,
    isFiltersDirty,
    isSearchPossible,
    clearSearch
  }
}

function useStakeholdersOverviewPageState() {
  /** Simple way to keep track of the current tab to navigate to the correct route when the user clicks the search button in the main navigation */
  const currentSearchTab = ref<'advanced' | 'quick'>('quick')
  return {
    currentSearchTab
  }
}

export interface StakeholderSearchContext {
  quickSearchState: ReturnType<typeof useQuickSearchState>
  advancedSearchState: ReturnType<typeof useAdvancedSearchState>
  pageState: ReturnType<typeof useStakeholdersOverviewPageState>
}

/**
 * Build the stakeholder search context (quick + advanced search state and
 * overview page state). Constructed once per `StakeholderSearchProvider` and
 * provided via `provideStakeholderSearch`.
 */
export function defineStakeholderSearchContext(): StakeholderSearchContext {
  const advancedSearchState = useAdvancedSearchState()

  const quickSearchState = useQuickSearchState()

  const pageState = useStakeholdersOverviewPageState()

  return {
    quickSearchState,
    advancedSearchState,
    pageState
  }
}

export const [useStakeholderSearch, provideStakeholderSearch] =
  createContext<StakeholderSearchContext>('gs-stakeholders-search')
